/**
 * O que faz: Página "Inteligência Artificial" organizada em cinco abas de seção, cada uma com um trabalho:
 *            Ferramentas (conteúdo com IA), Assistente (atendimento no WhatsApp), Agentes (revisão e
 *            configuração dos classificadores), Base de conhecimento e Métricas (resultado dos agentes).
 * Onde usa: Menu lateral > Inteligência Artificial na rota /ia. A aba ativa fica na URL (?aba=agentes),
 *           e ?revisar=1 abre direto o modal de revisão (link do sino de avisos).
 * Conexões: usePlanPermissions, AISettingsForm, SecaoAgentes, ResultadosAgentes, RestrictedFeature.
 *           Visual: Gens Design System via .gens-escopo, ligado no <body> por EscopoVisualAdmin.
 * Tabelas: subscriptions, plans, clinicas e user_profiles via usePlanPermissions; as abas leem só quando abertas.
 * Autor: IA
 * Data: 22/04/2026 (reorganizada em abas no design system Gens em 26/09/2026).
 */

import { useCallback, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowRight, BarChart3, Bot, Brain, FileText, Globe2, MessageSquareText, ScanSearch, Scissors, Sparkles, Wand2 } from 'lucide-react';
import { AISettingsForm } from '@/components/settings/AISettingsForm';
import { RestrictedFeature } from '@/components/ui/RestrictedFeature';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useClinica } from '@/contexts/ClinicaContext';
import { useAdminClinicaPersistente } from '@/hooks/useAdminClinicaPersistente';
import { AdminClinicSelector } from '@/components/admin/AdminClinicSelector';
import { ResultadosAgentes } from '@/features/agentes/components/ResultadosAgentes';
import { SecaoAgentes } from '@/features/agentes/components/SecaoAgentes';
import { usePlanPermissions } from '@/hooks/usePlanPermissions';
import { BaseConhecimentoClinica } from '@/features/conhecimento/BaseConhecimentoClinica';

type Ferramenta = {
  id: string;
  titulo: string;
  descricao: string;
  caminho: string;
  icone: typeof Sparkles;
  somenteAdmin?: boolean;
};

// Lista única das ferramentas de conteúdo: mesma aparência para todas, sem cor por ferramenta,
// porque no design system a cor comunica estado e ação, não enfeite.
const FERRAMENTAS: Ferramenta[] = [
  { id: 'legendas', titulo: 'Gerador de Legendas', descricao: 'Envie vídeos ou imagens e receba legendas prontas para as redes, com tom ajustável.', caminho: '/gerador-legendas', icone: Sparkles },
  { id: 'analise-video', titulo: 'Análise de Vídeo', descricao: 'Transcreva o áudio, avalie a narrativa e receba uma leitura de marketing do criativo.', caminho: '/analise-video', icone: ScanSearch },
  { id: 'formatar-video', titulo: 'Formatar Vídeo', descricao: 'Converta vídeos verticais para 4:5 com fundo desfocado para render melhor no feed.', caminho: '/formatar-video', icone: Scissors },
  { id: 'anuncios', titulo: 'Gerador de Anúncios', descricao: 'Crie anúncios persuasivos a partir de PDFs, links e materiais de referência.', caminho: '/gerador-anuncios', icone: FileText },
  { id: 'seo', titulo: 'SEO de Perfil', descricao: 'Analise site, Google local e Instagram e descubra melhorias rápidas de presença.', caminho: '/seo-perfil', icone: Globe2 },
  { id: 'marketing-ai', titulo: 'Marketing AI', descricao: 'Hub administrativo com chat, documentos, skills e análises avançadas de campanha.', caminho: '/admin/marketing-ai', icone: Brain, somenteAdmin: true },
];

// Abas válidas na URL; qualquer outro valor cai na primeira, sem quebrar a página.
const ABAS = ['ferramentas', 'assistente', 'conhecimento', 'agentes', 'metricas'] as const;
type Aba = typeof ABAS[number];
const ehAba = (valor: string | null): valor is Aba => ABAS.includes(valor as Aba);

// Abas de seção (item 9.5 do design system): texto de apoio, ativa em tinta com barra lima profunda.
// A barra é um pseudo-elemento que cresce do centro (scale-x), então a troca de aba nunca é seca.
const CLASSE_ABA = 'relative h-11 gap-2 rounded-none bg-transparent px-1 text-sm font-medium text-muted-foreground shadow-none hover:text-foreground '
  + 'after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:origin-center after:scale-x-0 after:rounded-full after:bg-[#8fb83a] after:transition-transform after:duration-300 after:ease-out motion-reduce:after:transition-none '
  + 'data-[state=active]:bg-transparent data-[state=active]:font-semibold data-[state=active]:text-foreground data-[state=active]:shadow-none data-[state=active]:after:scale-x-100';

// O conteúdo entra deslizando do lado da aba escolhida: aba à direita vem da direita, e vice-versa.
const CLASSE_ENTRADA = {
  direita: 'data-[state=active]:slide-in-from-right-6',
  esquerda: 'data-[state=active]:slide-in-from-left-6',
} as const;

const ConfiguracoesIAPage = () => {
  const navigate = useNavigate();
  const [parametros, setParametros] = useSearchParams();
  // Mesma origem de verdade das permissões usada no restante do app.
  const { canConfigureAI, isPMKPlan, isAdmin, hasAIOverride, isLoading, planName } = usePlanPermissions();
  const { clinicaAtiva } = useClinica();
  const { clinicaSelecionada, selecionarClinica, isLoading: carregandoSelecaoAdmin } = useAdminClinicaPersistente();
  // Agentes e métricas usam o mesmo destino explícito do Pipeline; nunca caem na clínica pessoal do admin.
  const clinicaAgentesId = isAdmin ? clinicaSelecionada?.id : clinicaAtiva?.id;
  // Agentes fazem parte dos planos PMK e Premium; o banco confere o plano de novo ao salvar.
  const podeUsarAgentes = isAdmin || isPMKPlan || (planName ?? '').trim().toLowerCase() === 'premium';

  // Link antigo "/ia#agentes" continua funcionando: o hash vira a aba.
  const abaDaUrl = parametros.get('aba') ?? (typeof window !== 'undefined' && window.location.hash === '#agentes' ? 'agentes' : null);
  const abaAtiva: Aba = ehAba(abaDaUrl) ? abaDaUrl : 'ferramentas';
  const abrirRevisao = parametros.get('revisar') === '1';

  // Direção da última troca, só para escolher o lado de onde o conteúdo novo entra.
  const [direcao, setDirecao] = useState<keyof typeof CLASSE_ENTRADA>('direita');

  const trocarAba = (valor: string) => {
    setDirecao(ABAS.indexOf(valor as Aba) >= ABAS.indexOf(abaAtiva) ? 'direita' : 'esquerda');
    const proximos = new URLSearchParams(parametros);
    proximos.set('aba', valor);
    proximos.delete('revisar');
    // replace: trocar de aba não deve encher o histórico do botão voltar.
    setParametros(proximos, { replace: true });
  };

  // Depois que o modal abriu, tira o ?revisar da URL para não reabrir ao recarregar.
  const limparPedidoRevisao = useCallback(() => {
    setParametros((atuais) => {
      const proximos = new URLSearchParams(atuais);
      proximos.delete('revisar');
      return proximos;
    }, { replace: true });
  }, [setParametros]);

  const ferramentasVisiveis = FERRAMENTAS.filter((ferramenta) => !ferramenta.somenteAdmin || isAdmin);

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 sm:px-6 lg:px-8" role="status" aria-label="Carregando Inteligência Artificial">
        <div className="h-8 w-64 animate-pulse rounded-xl bg-muted" />
        <div className="h-4 w-96 max-w-full animate-pulse rounded-lg bg-muted" />
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, indice) => <div key={indice} className="h-36 animate-pulse rounded-3xl bg-muted" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl space-y-6 px-4 py-6 sm:px-6 lg:px-8">
      {/* Cabeçalho de página (9.13): título, uma linha de descrição; sem banner nem KPI decorativo. */}
      <header className="space-y-1">
        <p className="lg-rotulo flex items-center gap-1.5"><Wand2 className="h-3.5 w-3.5" aria-hidden="true" />Inteligência Artificial</p>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">Tudo o que a IA faz pela sua clínica</h1>
        <p className="max-w-2xl text-sm text-muted-foreground">Crie conteúdo, configure o assistente do WhatsApp e acompanhe os agentes que organizam o seu funil.</p>
      </header>

      {/* Reutiliza o seletor compartilhado; nome visível evita configurar a unidade errada sem perceber. */}
      {isAdmin && (abaAtiva === 'agentes' || abaAtiva === 'metricas') && (
        <section aria-label="Clínica dos agentes" className="lg-card space-y-3 rounded-2xl border border-border bg-card p-5">
          <p className="text-sm font-medium">Selecione a clínica para configurar os agentes e consultar suas métricas.</p>
          <AdminClinicSelector clinicaSelecionada={clinicaSelecionada} onClinicaSelected={selecionarClinica} showStats={false} />
        </section>
      )}

      <Tabs value={abaAtiva} onValueChange={trocarAba} className="space-y-6">
        <TabsList className="conhecimento-abas-scroll h-auto w-full justify-start gap-6 overflow-x-auto rounded-none border-b border-border bg-transparent p-0">
          <TabsTrigger value="ferramentas" className={CLASSE_ABA}><Sparkles className="h-4 w-4" aria-hidden="true" />Ferramentas</TabsTrigger>
          <TabsTrigger value="assistente" className={CLASSE_ABA}><MessageSquareText className="h-4 w-4" aria-hidden="true" />Assistente</TabsTrigger>
          <TabsTrigger value="conhecimento" className={CLASSE_ABA}><FileText className="h-4 w-4" aria-hidden="true" />Base de conhecimento</TabsTrigger>
          <TabsTrigger value="agentes" className={CLASSE_ABA}><Bot className="h-4 w-4" aria-hidden="true" />Agentes</TabsTrigger>
          <TabsTrigger value="metricas" className={CLASSE_ABA}><BarChart3 className="h-4 w-4" aria-hidden="true" />Métricas</TabsTrigger>
        </TabsList>

        {/* Ferramentas de conteúdo: cada card é um atalho; o card inteiro é o botão. */}
        <TabsContent value="ferramentas" className={`mt-0 space-y-4 ${CLASSE_ENTRADA[direcao]}`}>
          <p className="text-sm text-muted-foreground">Escolha uma ferramenta para criar ou melhorar um criativo.</p>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {ferramentasVisiveis.map((ferramenta, indice) => {
              const Icone = ferramenta.icone;
              return (
                <button
                  key={ferramenta.id}
                  type="button"
                  onClick={() => navigate(ferramenta.caminho)}
                  // Entrada em cascata curta (40 ms por card) e leve subida no hover: só transform/opacidade/sombra.
                  style={{ animationDelay: `${indice * 40}ms` }}
                  className="lg-card group flex h-full flex-col gap-4 rounded-3xl border border-border bg-card p-5 text-left animate-in fade-in-0 slide-in-from-bottom-2 fill-mode-both duration-300 transition-[transform,box-shadow,border-color] ease-out hover:-translate-y-0.5 hover:border-input motion-reduce:animate-none motion-reduce:transition-none motion-reduce:hover:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <div className="flex items-start justify-between gap-3">
                    <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-accent text-accent-foreground">
                      <Icone className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <ArrowRight className="h-4 w-4 text-muted-foreground transition-transform group-hover:translate-x-0.5 motion-reduce:transition-none" aria-hidden="true" />
                  </div>
                  <div className="space-y-1.5">
                    <p className="flex items-center gap-2 font-semibold text-foreground">
                      {ferramenta.titulo}
                      {ferramenta.somenteAdmin && <span className="lg-selo lg-selo--neutro">Admin</span>}
                    </p>
                    <p className="text-sm leading-relaxed text-muted-foreground">{ferramenta.descricao}</p>
                  </div>
                </button>
              );
            })}
          </div>
        </TabsContent>

        {/* Assistente do WhatsApp: mesmo formulário usado no admin da clínica. */}
        <TabsContent value="assistente" className={`mt-0 space-y-4 ${CLASSE_ENTRADA[direcao]}`}>
          <p className="text-sm text-muted-foreground">Como o assistente responde os leads no WhatsApp: ativação, horários, personalidade e ritmo.</p>
          {canConfigureAI ? (
            <AISettingsForm />
          ) : (
            <RestrictedFeature
              planName={planName}
              featureName="Configurações Avançadas de IA"
              isAdmin={isAdmin}
              hasOverride={hasAIOverride}
              showUpgrade={!isPMKPlan}
              onRequestAccess={() => console.log('[ConfiguracoesIAPage] Solicitação de acesso às configurações avançadas de IA.')}
            />
          )}
        </TabsContent>

        {/* Chave por clínica desmonta editor/microfone ao trocar de clínica e impede misturar rascunhos. */}
        <TabsContent value="conhecimento" className={`mt-0 ${CLASSE_ENTRADA[direcao]}`}>
          {clinicaAtiva?.id ? <BaseConhecimentoClinica key={clinicaAtiva.id} clinicaId={clinicaAtiva.id} />
            : <p role="status" className="text-sm text-muted-foreground">Selecione uma clínica para abrir a base de conhecimento.</p>}
        </TabsContent>

        {/* Agentes: revisão no topo, depois configuração. */}
        <TabsContent value="agentes" className={`mt-0 ${CLASSE_ENTRADA[direcao]}`}>
          {isAdmin && (carregandoSelecaoAdmin || !clinicaAgentesId) ? (
            <p role="status">{carregandoSelecaoAdmin ? 'Carregando a clínica selecionada…' : 'Selecione uma clínica para configurar os agentes.'}</p>
          ) : podeUsarAgentes && clinicaAgentesId ? (
            // A chave descarta rascunhos, sugestões e modais da clínica anterior ao trocar o destino.
            <SecaoAgentes key={clinicaAgentesId} clinicaId={clinicaAgentesId} modoAdmin={isAdmin} abrirRevisao={abrirRevisao} onAbriuRevisao={limparPedidoRevisao} />
          ) : (
            <RestrictedFeature
              planName={planName}
              featureName="Agentes de classificação"
              isAdmin={isAdmin}
              hasOverride={false}
              showUpgrade
              onRequestAccess={() => console.log('[ConfiguracoesIAPage] Solicitação de acesso aos agentes.')}
            />
          )}
        </TabsContent>

        {/* Métricas: resultado dos agentes e atalho para a análise de conversas. */}
        <TabsContent value="metricas" className={`mt-0 space-y-4 ${CLASSE_ENTRADA[direcao]}`}>
          {isAdmin && (carregandoSelecaoAdmin || !clinicaAgentesId) ? (
            <p role="status">{carregandoSelecaoAdmin ? 'Carregando a clínica selecionada…' : 'Selecione uma clínica para consultar as métricas dos agentes.'}</p>
          ) : podeUsarAgentes && clinicaAgentesId ? (
            <>
              <Card className="lg-card">
                <CardHeader className="space-y-1">
                  <CardTitle className="text-base">Resultados dos agentes</CardTitle>
                  <CardDescription>O que os agentes fizeram, quanto a equipe concordou, o que os retornos trouxeram e quanto custou.</CardDescription>
                </CardHeader>
                <CardContent><ResultadosAgentes key={clinicaAgentesId} clinicaId={clinicaAgentesId} modoAdmin={isAdmin} /></CardContent>
              </Card>
              <Card className="lg-card">
                <CardContent className="flex flex-wrap items-center justify-between gap-4 p-5">
                  <div className="space-y-1">
                    <p className="font-semibold text-foreground">Análise de conversas</p>
                    <p className="text-sm text-muted-foreground">Tempo de resposta, leads esperando e retomadas, conversa por conversa.</p>
                  </div>
                  <Button asChild variant="outline" className="rounded-full">
                    <Link to="/analises/conversas">Abrir análise<ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" /></Link>
                  </Button>
                </CardContent>
              </Card>
            </>
          ) : (
            <RestrictedFeature
              planName={planName}
              featureName="Métricas dos agentes"
              isAdmin={isAdmin}
              hasOverride={false}
              showUpgrade
              onRequestAccess={() => console.log('[ConfiguracoesIAPage] Solicitação de acesso às métricas dos agentes.')}
            />
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConfiguracoesIAPage;
